import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Eye, Star, UserCog, KeyRound, Trash2, Plus, StickyNote
} from 'lucide-react';
import { STATUS_COLORS, PLAN_COLORS, NOTE_CATEGORIES } from './constants';

export function TenantDetailView({
  tenant,
  tenantUsers,
  tenantNotes,
  onBack,
  onImpersonate,
  onToggleInternal,
  onToggleActive,
  showResetPw, setShowResetPw,
  newPassword, setNewPassword,
  onResetPassword,
  editUser, setEditUser,
  editForm, setEditForm,
  onUpdateUser,
  deleteUser, setDeleteUser,
  onDeleteUser,
  newNote, setNewNote,
  noteCategory, setNoteCategory,
  onAddNote,
  onDeleteNote,
}) {
  return (
    <div className="space-y-6 p-1" data-testid="tenant-detail-view">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-tenants-btn">
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white font-bold text-lg">
            {(tenant.company_name || '?')[0].toUpperCase()}
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2" data-testid="tenant-detail-name">
              {tenant.company_name}
              {tenant.is_internal && (
                <Badge className="bg-warning/10 text-warning border border-warning/30 text-[10px]">
                  <Star size={10} className="mr-0.5 fill-amber-500" />Internal
                </Badge>
              )}
            </h1>
            <p className="text-xs text-muted-foreground">{tenant.email} &middot; {tenant.country || 'N/A'}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button size="sm" className="bg-warning hover:bg-warning text-white" onClick={() => onImpersonate(tenant)} data-testid="impersonate-btn">
          <Eye size={14} className="mr-1.5" /> View as Tenant
        </Button>
        <Button size="sm" variant="outline" onClick={() => onToggleInternal(tenant.id, tenant.is_internal)} data-testid="toggle-internal-detail-btn">
          <Star size={14} className={`mr-1.5 ${tenant.is_internal ? 'fill-amber-500 text-warning' : ''}`} />
          {tenant.is_internal ? 'Remove Internal' : 'Mark Internal'}
        </Button>
        <Button
          size="sm" variant="outline"
          onClick={() => onToggleActive(tenant.id, tenant.is_active)}
          className={tenant.is_active ? 'text-error border-error/30 hover:bg-error/10' : 'text-success border-success/30 hover:bg-success/10'}
          data-testid="toggle-active-detail-btn"
        >
          {tenant.is_active ? 'Suspend' : 'Activate'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Tenant Info */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground">Tenant Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><Badge className={PLAN_COLORS[tenant.plan] || 'bg-muted'}>{tenant.is_internal ? 'Internal (Free)' : tenant.plan}</Badge></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge className={STATUS_COLORS[tenant.subscription_status] || STATUS_COLORS.trial}>{tenant.subscription_status}</Badge></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Users</span><span className="font-medium text-foreground">{tenant.user_count || 0}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Branches</span><span className="font-medium text-foreground">{tenant.branch_count || 0}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span className="font-medium text-foreground">{new Date(tenant.created_at).toLocaleDateString()}</span></div>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
              <UserCog size={14} /> Users ({tenantUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tenantUsers.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No users found</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {tenantUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-info"
                        onClick={() => { setEditUser(u); setEditForm({ name: u.name, email: u.email, role: u.role || 'operator' }); }}
                        title="Edit user" data-testid={`edit-user-${u.id}`}>
                        <UserCog size={13} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-orange-500"
                        onClick={() => { setShowResetPw(u); setNewPassword(''); }}
                        title="Reset password" data-testid={`reset-pw-${u.id}`}>
                        <KeyRound size={13} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-error"
                        onClick={() => setDeleteUser(u)}
                        title="Delete user" data-testid={`delete-user-${u.id}`}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Support Notes */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
              <StickyNote size={14} /> Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex gap-2">
              <Select value={noteCategory} onValueChange={setNoteCategory}>
                <SelectTrigger className="w-24 h-8 text-xs" data-testid="note-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_CATEGORIES.map(c => (
                    <SelectItem key={c} value={c} className="text-xs capitalize">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Add note..."
                className="flex-1 min-h-[32px] h-8 text-xs resize-none"
                data-testid="new-note-input"
              />
              <Button size="sm" className="h-8 w-8 p-0" onClick={onAddNote} disabled={!newNote.trim()} data-testid="add-note-btn">
                <Plus size={14} />
              </Button>
            </div>
            {tenantNotes.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-3">No notes yet</p>
            ) : (
              <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                {tenantNotes.map(n => (
                  <div key={n.id} className="p-2 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-start justify-between gap-1">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Badge variant="outline" className="text-[9px] capitalize">{n.category}</Badge>
                          <span className="text-[9px] text-muted-foreground">{new Date(n.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-xs text-foreground line-clamp-2">{n.content}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-error shrink-0"
                        onClick={() => onDeleteNote(n.id)} data-testid={`delete-note-${n.id}`}>
                        <Trash2 size={10} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={!!showResetPw} onOpenChange={() => setShowResetPw(null)}>
        <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Reset password for <strong>{showResetPw?.name}</strong> ({showResetPw?.email})
            </p>
            <Input
              type="password"
              placeholder="New password (min 6 chars)"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              data-testid="new-password-input"
            />
            <p className="text-xs text-muted-foreground">User will be prompted to change their password on next login.</p>
            <Button className="w-full" onClick={onResetPassword} disabled={newPassword.length < 6} data-testid="confirm-reset-pw-btn">
              Reset Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="max-w-sm" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Name</label>
              <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} data-testid="edit-user-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Email</label>
              <Input value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} data-testid="edit-user-email" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Role</label>
              <Select value={editForm.role} onValueChange={v => setEditForm(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="cashier">Cashier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={onUpdateUser} data-testid="confirm-edit-user-btn">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-base text-error">Delete User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete <strong>{deleteUser?.name}</strong> ({deleteUser?.email})? This action cannot be undone.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteUser(null)}>Cancel</Button>
              <Button variant="destructive" className="flex-1" onClick={onDeleteUser} data-testid="confirm-delete-user-btn">Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
